import type { ZoriModuleManifest } from "../../app/module-manifest";

export const ORGANIZATION_HUB_PATH = "/organization";
export const ORGANIZATION_ORG_ALIAS_PATH = "/org";
export const ORGANIZATION_WORKSPACES_PATH = "/workspaces";
export const ORGANIZATION_ESPACIOS_ALIAS_PATH = "/espacios";
export const ORGANIZATION_WORKSPACE_DETAIL_PATH = "/workspaces/:id";
export const ORGANIZATION_ESPACIOS_DETAIL_ALIAS_PATH = "/espacios/:id";
export const ORGANIZATION_WORKSPACE_ADMIN_ALIAS_PATH = "/workspace-admin";
export const ORGANIZATION_MEMBERS_PATH = "/members";
export const ORGANIZATION_MEMBER_DETAIL_PATH = "/members/:id";
export const ORGANIZATION_PERSONAS_ALIAS_PATH = "/personas";
export const ORGANIZATION_ORG_USERS_ALIAS_PATH = "/org-users";
export const ORGANIZATION_TEAMS_PATH = "/teams";
export const ORGANIZATION_TEAM_DETAIL_PATH = "/teams/:id";
export const ORGANIZATION_DIRECTORY_PATH = "/directory";
export const ORGANIZATION_ROLES_PATH = "/roles";
export const ORGANIZATION_ORG_ROLES_ALIAS_PATH = "/org-roles";
export const ORGANIZATION_ORG_TEAMS_ALIAS_PATH = "/org-teams";

/**
 * The Organization module ships the surfaces backed by mounted v2 platform
 * contracts: workspace administration (`/workspaces` + `/workspaces/:id`
 * detail with member directory, agent bindings, settings, and an audit tail),
 * the organization member directory (`/members`), and role administration
 * (`/roles`). `/espacios`, `/espacios/:id`, `/workspace-admin`, `/personas`,
 * `/org-users`, `/org-roles`, and `/org-teams` are owned aliases
 * canonicalizing to the React paths.
 *
 * Capability gates mirror the server-enforced permissions: `workspaces:read`,
 * `memberships:read`, and `roles:read` for reads; the matching `:write`/
 * `:delete` capabilities gate mutations inside the pages.
 */
export const organizationModuleManifest: ZoriModuleManifest = {
  id: "organization",
  labelKey: "zori.modules.organization",
  requiredCapabilities: ["workspace.view"],
  routes: [
    {
      id: "organization-hub",
      path: ORGANIZATION_HUB_PATH,
      labelKey: "zori.modules.organization",
      navLabelKey: "zori.nav.overview",
      requiredCapabilities: ["workspaces:read"],
      navGroup: "organization",
      icon: "building",
    },
    {
      id: "org-alias",
      path: ORGANIZATION_ORG_ALIAS_PATH,
      labelKey: "zori.modules.organization",
      canonicalRouteId: "organization:organization-hub",
      requiredCapabilities: ["workspaces:read"],
      navHidden: true,
    },
    {
      id: "workspaces",
      path: ORGANIZATION_WORKSPACES_PATH,
      labelKey: "zori.organization.workspaces.page",
      requiredCapabilities: ["workspaces:read"],
      navGroup: "organization",
      icon: "building",
    },
    {
      id: "workspace-detail",
      path: ORGANIZATION_WORKSPACE_DETAIL_PATH,
      labelKey: "zori.organization.workspaces.page",
      canonicalRouteId: "organization:workspaces",
      context: { resourceType: "workspace", idParam: "id" },
      requiredCapabilities: ["workspaces:read"],
      navHidden: true,
    },
    {
      id: "members",
      path: ORGANIZATION_MEMBERS_PATH,
      labelKey: "zori.organization.members.page",
      requiredCapabilities: ["memberships:read"],
      navGroup: "organization",
      icon: "people",
    },
    {
      id: "member-detail",
      path: ORGANIZATION_MEMBER_DETAIL_PATH,
      labelKey: "zori.organization.memberDetail.page",
      canonicalRouteId: "organization:members",
      context: { resourceType: "user", idParam: "id" },
      requiredCapabilities: ["memberships:read"],
      navHidden: true,
    },
    {
      id: "teams",
      path: ORGANIZATION_TEAMS_PATH,
      labelKey: "zori.organization.teams.page",
      requiredCapabilities: ["memberships:read"],
      navGroup: "organization",
      icon: "people",
    },
    {
      id: "team-detail",
      path: ORGANIZATION_TEAM_DETAIL_PATH,
      labelKey: "zori.organization.teamDetail.page",
      canonicalRouteId: "organization:teams",
      context: { resourceType: "team", idParam: "id" },
      requiredCapabilities: ["memberships:read"],
      navHidden: true,
    },
    {
      id: "directory",
      path: ORGANIZATION_DIRECTORY_PATH,
      labelKey: "zori.organization.directory.page",
      requiredCapabilities: ["memberships:read"],
      navGroup: "organization",
      icon: "search",
    },
    {
      id: "roles",
      path: ORGANIZATION_ROLES_PATH,
      labelKey: "zori.organization.roles.page",
      requiredCapabilities: ["roles:read"],
      navGroup: "organization",
      icon: "shield",
    },
    {
      id: "espacios-alias",
      path: ORGANIZATION_ESPACIOS_ALIAS_PATH,
      labelKey: "zori.organization.workspaces.page",
      canonicalRouteId: "organization:workspaces",
      requiredCapabilities: ["workspaces:read"],
      navHidden: true,
    },
    {
      id: "espacios-detail-alias",
      path: ORGANIZATION_ESPACIOS_DETAIL_ALIAS_PATH,
      labelKey: "zori.organization.workspaces.page",
      canonicalRouteId: "organization:workspaces",
      requiredCapabilities: ["workspaces:read"],
      navHidden: true,
    },
    {
      id: "workspace-admin-alias",
      path: ORGANIZATION_WORKSPACE_ADMIN_ALIAS_PATH,
      labelKey: "zori.organization.workspaces.page",
      canonicalRouteId: "organization:workspaces",
      requiredCapabilities: ["workspaces:read"],
      navHidden: true,
    },
    {
      id: "personas-alias",
      path: ORGANIZATION_PERSONAS_ALIAS_PATH,
      labelKey: "zori.organization.members.page",
      canonicalRouteId: "organization:members",
      requiredCapabilities: ["memberships:read"],
      navHidden: true,
    },
    {
      id: "org-users-alias",
      path: ORGANIZATION_ORG_USERS_ALIAS_PATH,
      labelKey: "zori.organization.members.page",
      canonicalRouteId: "organization:members",
      requiredCapabilities: ["memberships:read"],
      navHidden: true,
    },
    {
      id: "org-roles-alias",
      path: ORGANIZATION_ORG_ROLES_ALIAS_PATH,
      labelKey: "zori.organization.roles.page",
      canonicalRouteId: "organization:roles",
      requiredCapabilities: ["roles:read"],
      navHidden: true,
    },
    {
      id: "org-teams-alias",
      path: ORGANIZATION_ORG_TEAMS_ALIAS_PATH,
      labelKey: "zori.organization.teams.page",
      canonicalRouteId: "organization:teams",
      requiredCapabilities: ["memberships:read"],
      navHidden: true,
    },
  ],
};
